import React, { useState, useEffect } from 'react';
import { 
  Box,
  Text,
  Stack,
  Group,
  Select,
  Switch,
  Paper,
  Table,
  Center,
  LoadingOverlay,
  ActionIcon,
  Tooltip,
  Code,
  Loader,
  Button,
  Indicator,
  Progress,
} from '@mantine/core';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { IconDatabaseOff, IconAdjustments, IconFilter, IconPlus, IconRun, IconRefresh, IconX, IconSettings, IconPlayerPlay, IconSearch } from '@tabler/icons';
import { SourceIcon } from '../../../../utils/SourceIcon';
import { FieldMappingDrawer } from './FieldMappingDrawer';
import { ExploreFieldsModal } from './ExploreFieldsModal';
import { useBagelObjects } from '../../../../hooks/useBagelObjects';
import { useAuth0 } from '@auth0/auth0-react';
import { AvailableIntegration, Integration, ExternalObjectConf, BagelObjectConf } from '@bageltechnologies/bagel-common';
import { FilterPopup } from './FilterPopup';
import { useGlobalAccounts } from '../../../../hooks/useGlobalAccounts';
import { useTriggerIntegrationSyncRun, useIntegrationObjectTasks, useIntegrationTasks, useUpdateIntegrationObjectMeta, useUnprocessedItemCount, useProcessUnprocessedItems, usePrepareIntegrationMeta, useUpdateExternalObject } from '../../../../hooks/useIntegrations';
import { IntegrationSyncRunsTimeline } from './IntegrationSyncRuns/IntegrationSyncRunsTimeline';
import { RunModal } from './IntegrationSyncRuns/RunModal';
import ProcessItemsModal from './IntegrationSyncRuns/ProcessItemsModal';

// interface BagelObject {
//   key: string;
//   label: string;
//   collection: string;
//   fields: Array<{
//     name: string;
//     fieldPath: string;
//     type: string;
//     defaultValue?: string;
//   }>;
// }

interface MappingsTabProps {
  integration: {
    available: AvailableIntegration;
    connected?: Integration;
  };
  onSaveMapping: (mappingData: ExternalObjectConf[]) => Promise<void>;
  isUpdatingMapping: boolean;
}

export const MappingsTab: React.FC<MappingsTabProps> = ({
  integration,
  onSaveMapping,
  isUpdatingMapping,
}) => {
  const { available, connected } = integration;
  const auth0 = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // State for external objects mapping
  const [externalObjects, setExternalObjects] = useState<ExternalObjectConf[]>([]);
  const [selectedObject, setSelectedObject] = useState<ExternalObjectConf | null>(null);
  const [selectedBagelObject, setSelectedBagelObject] = useState<BagelObjectConf | null>(null);
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
  const [isAddMode, setIsAddMode] = useState(false);
  const [newObjectMapping, setNewObjectMapping] = useState<{
    objectId: string;
    bagelObjectKey: string;
  }>({
    objectId: '',
    bagelObjectKey: '',
  });

  // State for filter popup
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [currentFilterObject, setCurrentFilterObject] = useState<ExternalObjectConf | null>(null);

  // State for explore fields modal
  const [exploreModalOpen, setExploreModalOpen] = useState(false);
  const [exploreObjectKey, setExploreObjectKey] = useState<string | null>(null);

  // Get drawer state from URL
  const isFieldDrawerOpen = searchParams.get('drawer') === 'field-mapping';
  const selectedObjectId = searchParams.get('objectId');

  const {
    data: bagelObjects,
    isLoading: isBagelObjectsLoading,
    isError: isBagelObjectsError,
  } = useBagelObjects(auth0);
  
  // Hook for updating external objects via new endpoint
  const updateExternalObjectMutation = useUpdateExternalObject();
  
  // Initialize state from connected integration or default objects
  useEffect(() => {
    if (connected?.externalObjects) {
      setExternalObjects(connected.externalObjects);
    } else {
      setExternalObjects([]);
    }
  }, [connected]);

  // Update selected object when URL changes
  useEffect(() => {
    if (selectedObjectId && bagelObjects) {
      const externalObj = externalObjects.find(obj => obj.originObject === selectedObjectId);
      if (externalObj) {
        setSelectedObject(externalObj);
        if (externalObj.bagelObjectKey) {
          const matchingBagelObject = bagelObjects.find((obj: BagelObjectConf) => obj.key === externalObj.bagelObjectKey);
          setSelectedBagelObject(matchingBagelObject || null);
        }
      }
    } else {
      setSelectedObject(null);
      setSelectedBagelObject(null);
    }
  }, [selectedObjectId, externalObjects, bagelObjects]);
  

  const handleOpenDrawer = (externalObj: ExternalObjectConf) => {
    console.log({externalObj})
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('drawer', 'field-mapping');
    newSearchParams.set('objectId', externalObj.originObject);
    navigate(`${location.pathname}?${newSearchParams.toString()}`);
  };

  const handleCloseDrawer = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('drawer');
    newSearchParams.delete('objectId');
    navigate(`${location.pathname}?${newSearchParams.toString()}`);
  };

  const handleOpenFilterModal = (externalObj: ExternalObjectConf) => {
    setCurrentFilterObject(externalObj as ExternalObjectConf);
    setFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
    setCurrentFilterObject(null);
  };

  const handleOpenExploreModal = (externalObj: ExternalObjectConf) => {
    setExploreObjectKey(externalObj.originObject);
    setExploreModalOpen(true);
  };

  const handleCloseExploreModal = () => {
    setExploreModalOpen(false);
    setExploreObjectKey(null);
  };

  // Utility function to update a single external object
  const updateExternalObject = async (originObject: string, updates: Partial<ExternalObjectConf>) => {
    const targetObject = externalObjects.find(obj => obj.originObject === originObject);
    if (targetObject) {
      const updatedObject = { ...targetObject, ...updates };
      const updatedObjects = externalObjects.map(obj => 
        obj.originObject === originObject 
          ? updatedObject 
          : obj
      );
      setLoadingStates(prev => ({ ...prev, [originObject]: true }));
      setExternalObjects(updatedObjects);
      
      try {
        await onSaveMapping(updatedObjects);
      } catch (error) {
        console.error('Failed to save mapping:', error);
        // Optionally revert the state on error
        setExternalObjects(externalObjects);
      } finally {
        setLoadingStates(prev => ({ ...prev, [originObject]: false }));
      }
    } else {
      console.error('Object not found for originObject:', originObject);
    }
  };

  // Helper function to update Ready to Process using new endpoint
  const updateReadyToProcess = async (originObject: string, checked: boolean) => {
    setLoadingStates(prev => ({ ...prev, [originObject]: true }));
    try {
      await updateExternalObjectMutation.mutateAsync({
        key: available.key,
        originObjectKey: originObject,
        updates: { readyToProcess: checked }
      });
      // Update local state on success
      setExternalObjects(prev => 
        prev.map(obj => 
          obj.originObject === originObject 
            ? { ...obj, readyToProcess: checked } as ExternalObjectConf
            : obj
        )
      );
    } catch (error) {
      console.error('Failed to update Ready to Process:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [originObject]: false }));
    }
  };

  // Helper function to update Ready to Sync using new endpoint
  const updateReadyToSync = async (originObject: string, checked: boolean) => {
    setLoadingStates(prev => ({ ...prev, [originObject]: true }));
    try {
      await updateExternalObjectMutation.mutateAsync({
        key: available.key,
        originObjectKey: originObject,
        updates: { readyToSync: checked }
      });
      // Update local state on success
      setExternalObjects(prev => 
        prev.map(obj => 
          obj.originObject === originObject 
            ? { ...obj, readyToSync: checked } as ExternalObjectConf
            : obj
        )
      );
    } catch (error) {
      console.error('Failed to update Ready to Sync:', error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [originObject]: false }));
    }
  };

  const handleAddNewMapping = () => {
    setIsAddMode(true);
  };

  const handleCancelAddMapping = () => {
    setIsAddMode(false);
    setNewObjectMapping({
      objectId: '',
      bagelObjectKey: '',
    });
  };

  const [isAddingMapping, setIsAddingMapping] = useState(false);

  const handleSubmitNewMapping = async () => {
    console.log({newObjectMapping})
    if (!newObjectMapping.objectId) return;
    
    setIsAddingMapping(true);

    const selectedMetaObject = connected?.metaObject?.availableObjects?.find(
      (obj: { name: string }) => obj.name === newObjectMapping.objectId || (obj as any).originObject === newObjectMapping.objectId
    );

    if (!selectedMetaObject) {
      setIsAddingMapping(false);
      return;
    }

    const newMapping: ExternalObjectConf = {
      ...selectedMetaObject,
      isDisabled: true,
      originObject: selectedMetaObject.originObject,
      bagelObjectKey: newObjectMapping.bagelObjectKey,
    };
    setLoadingStates(prev => ({ ...prev, [newMapping.originObject]: true }));
    
    try {
      const updatedObjects = [...externalObjects, newMapping];
      await onSaveMapping(updatedObjects);
      setExternalObjects(updatedObjects);
      setIsAddMode(false);
      setNewObjectMapping({
        objectId: '',
        bagelObjectKey: '',
      });
    } catch (error) {
      console.error('Failed to add new mapping:', error);
    } finally {
      setIsAddingMapping(false);
      setLoadingStates(prev => ({ ...prev, [newMapping.originObject]: false }));
    }
  };

  console.log({externalObjects})
  const renderEmptyState = () => (
    <Box py="xl">
      <Center>
        <Stack align="center" spacing="sm">
          <IconDatabaseOff size={40} color="#CED4DA" />
          <Text size="sm" color="dimmed">No objects available for mapping</Text>
          <Text size="xs" color="dimmed">
            There are no {available.name} objects configured for this integration
          </Text>
        </Stack>
      </Center>
    </Box>
  );

  // Replace run modal state and logic to use external objects
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [currentRunObject, setCurrentRunObject] = useState<ExternalObjectConf | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isLoadingObjects, setIsLoadingObjects] = useState(false);
  const [selectedObjects, setSelectedObjects] = useState<string[]>([]);

  // Prepare meta hook
  const prepareMetaMutation = usePrepareIntegrationMeta();
  const handlePrepareMeta = async () => {
    try {
      const result = await prepareMetaMutation.mutateAsync(available.key);
      console.log('Meta preparation result:', result);
      // Optionally show success notification or update UI based on result
    } catch (error) {
      console.error('Failed to prepare meta:', error);
      // Optionally show error notification
    }
  };

  const handleOpenRunModal = (externalObj: ExternalObjectConf) => {
    setCurrentRunObject(externalObj);
    setRunModalOpen(true);
  };
  const handleCloseRunModal = () => {
    setRunModalOpen(false);
    setCurrentRunObject(null);
  };
  const triggerSyncRun = useTriggerIntegrationSyncRun();
  const handleRun = async (dateRange?: { start: string; end: string; dateField?: string }) => {
    if (!currentRunObject) return;
    setIsRunning(true);
    await triggerSyncRun.mutateAsync({ 
      key: available.key, 
      objects: [currentRunObject.originObject],
      dateRange 
    });
    setIsRunning(false);
    handleCloseRunModal();
  };

  // Get active tasks for all objects to show task counts
  const { data: activeTasksData } = useIntegrationTasks(available.key, 0, 100, 'pending,in_progress');

  // Add MappingRow child component to handle per-row hooks
  const MappingRow = ({
    externalObj,
    availableKey,
    bagelObjects,
    loadingStates,
    isBagelObjectsLoading,
    isUpdatingMapping,
    updateExternalObject,
    handleOpenFilterModal,
    handleOpenExploreModal,
    handleOpenDrawer,
    handleOpenRunModal,
    activeTasks,
  }: {
    externalObj: ExternalObjectConf;
    availableKey: string;
    bagelObjects: BagelObjectConf[];
    loadingStates: { [key: string]: boolean };
    isBagelObjectsLoading: boolean;
    isUpdatingMapping: boolean;
    updateExternalObject: (originObject: string, updates: Partial<ExternalObjectConf>) => Promise<void>;
    handleOpenFilterModal: (externalObj: ExternalObjectConf) => void;
    handleOpenExploreModal: (externalObj: ExternalObjectConf) => void;
    handleOpenDrawer: (externalObj: ExternalObjectConf) => void;
    handleOpenRunModal: (externalObj: ExternalObjectConf) => void;
    activeTasks: any[];
  }) => {
    const taskCount = activeTasks.filter((task: any) =>
      task.objectName === externalObj.originObject
    ).length;

    // Refresh meta hook and loading state
    const updateMeta = useUpdateIntegrationObjectMeta();
    const [refreshLoading, setRefreshLoading] = useState(false);
    const handleRefreshMeta = async () => {
      setRefreshLoading(true);
      try {
        await updateMeta.mutateAsync({ key: availableKey, object: externalObj.originObject });
      } catch (e) {
        // Optionally show notification
      } finally {
        setRefreshLoading(false);
      }
    };

    // Process Items Modal state and hooks
    const [processModalOpen, setProcessModalOpen] = useState(false);
    const { data: processCounts, isLoading: isCountsLoading, isError: isCountsError, error: countsError, refetch: refetchCounts } = useUnprocessedItemCount(availableKey, externalObj.originObject || '');
    const processMutation = useProcessUnprocessedItems();
    const [processError, setProcessError] = useState<string | null>(null);



    const handleOpenProcessModal = () => {
      setProcessError(null);
      setProcessModalOpen(true);
      refetchCounts();
    };
    const handleCloseProcessModal = () => {
      setProcessModalOpen(false);
    };
    const handleProcess = async () => {
      setProcessError(null);
      try {
        await processMutation.mutateAsync({ key: availableKey, originObjectKey: externalObj.originObject || '' });
        refetchCounts();
      } catch (e: any) {
        setProcessError(e?.message || 'Failed to process items');
      }
    };

    return (
      <tr key={externalObj.originObject} style={{ opacity: loadingStates[externalObj.originObject] ? 0.5 : 1 }}>
        <td>
          <Group spacing="sm">
            {loadingStates[externalObj.originObject] ? (
              <Loader size={20} />
            ) : (
              <SourceIcon sourceName={availableKey} width={20} height={20} />
            )}
            <Box>
              <Text>{externalObj.label}</Text>
              {(externalObj.label !== externalObj.originObject) && <Text size="xs" color="dimmed">
                {externalObj.originObject} {externalObj.extraParams?.projectKey && `[${externalObj.extraParams?.projectKey}]`}
              </Text>}
            </Box>
          </Group>
        </td>
        <td>
          <Switch
            checked={externalObj.isDisabled !== true}
            onChange={(event) => updateExternalObject(externalObj.originObject, { isDisabled: !event.currentTarget.checked })}
            disabled={loadingStates[externalObj.originObject] || isBagelObjectsLoading}
          />
        </td>
        {/* Refresh Meta Column */}
        <td>
          <Tooltip label="Refresh object meta">
            <ActionIcon
              variant="outline"
              color="gray"
              onClick={handleRefreshMeta}
              disabled={refreshLoading || externalObj.isDisabled === true || loadingStates[externalObj.originObject] || isUpdatingMapping}
            >
              {refreshLoading ? <Loader size={16} /> : <IconRefresh size={16} className="icon-rotate-2" />}
            </ActionIcon>
          </Tooltip>
        </td>
        {/* Explore Fields Column */}
        <td>
          <Tooltip label="Explore fields">
            <ActionIcon
              variant="outline"
              color="blue"
              onClick={() => handleOpenExploreModal(externalObj)}
              disabled={externalObj.isDisabled === true || loadingStates[externalObj.originObject] || isUpdatingMapping || !externalObj.fieldsMeta?.length}
            >
              <IconSearch size={16} />
            </ActionIcon>
          </Tooltip>
        </td>
        {availableKey !== 'api' && (
          <td>
            <Tooltip label="Create sync preprocess run">
              <ActionIcon
                variant="outline"
                color="teal"
                onClick={() => handleOpenRunModal(externalObj)}
                disabled={externalObj.isDisabled === true || loadingStates[externalObj.originObject] || isUpdatingMapping}
              >
                <Indicator disabled={taskCount === 0} processing size={16} fz={8} color="teal" label={taskCount}>
                  <IconRun size={16} />
                </Indicator>
              </ActionIcon>
            </Tooltip>
          </td>
        )}
        {/* New Process Items column */}
        <td>
          <Tooltip label="Process items">
            <ActionIcon
              variant="outline"
              color={
                processCounts?.unprocessed === 0 && (processCounts?.processed || 0) > 0
                  ? "green"  // All processed
                  : (processCounts?.unprocessed || 0) > 0
                    ? "orange" // Some unprocessed
                    : "gray"   // No items or loading
              }
              onClick={handleOpenProcessModal}
              disabled={externalObj.isDisabled === true || loadingStates[externalObj.originObject] || isUpdatingMapping}
            >
              <IconPlayerPlay size={16} />
            </ActionIcon>
          </Tooltip>
          <ProcessItemsModal
            opened={processModalOpen}
            onClose={handleCloseProcessModal}
            processed={processCounts?.processed || 0}
            unprocessed={processCounts?.unprocessed || 0}
            isProcessing={processMutation.isPending}
            onProcess={handleProcess}
            error={processError || (isCountsError ? (countsError as any)?.message : null)}
          />
        </td>
        <td>
          <Tooltip label="Configure filters">
            <ActionIcon
              variant="outline"
              color="indigo"
              onClick={() => handleOpenFilterModal(externalObj)}
              disabled={externalObj.isDisabled === true || loadingStates[externalObj.originObject] || isUpdatingMapping}
            >
              <Indicator processing disabled={Boolean(!externalObj?.filters?.rules?.length)} size={16} fz={8} color="indigo" label={externalObj?.filters?.rules?.length || 0}>
                <IconFilter size={16} />
              </Indicator>
            </ActionIcon>
          </Tooltip>
        </td>
        <td>
          <Select
            data={bagelObjects?.map((obj) => ({
              label: obj.label,
              value: obj.key
            })) || []}
            value={externalObj.bagelObjectKey || ''}
            onChange={(value) => updateExternalObject(externalObj.originObject, { bagelObjectKey: value || '' })}
            placeholder="Select object"
            disabled={externalObj.isDisabled === true || loadingStates[externalObj.originObject] || isBagelObjectsLoading}
          />
        </td>
        <td>
          <Tooltip label="Ready to process items">
            <Switch
              checked={(externalObj as any).readyToProcess === true}
              onChange={(event) => updateReadyToProcess(externalObj.originObject, event.currentTarget.checked)}
              disabled={externalObj.isDisabled === true || loadingStates[externalObj.originObject] || isBagelObjectsLoading || !externalObj.bagelObjectKey}
            />
          </Tooltip>
        </td>
        <td>
          <Tooltip label="All configurations are done let's sync">
            <Switch
              checked={(externalObj as any).readyToSync === true}
              onChange={(event) => updateReadyToSync(externalObj.originObject, event.currentTarget.checked)}
              disabled={externalObj.isDisabled === true || loadingStates[externalObj.originObject] || isBagelObjectsLoading || !externalObj.bagelObjectKey}
            />
          </Tooltip>
        </td>
        <td style={{ justifyItems: 'right' }}>
          <Tooltip label="Map fields">
            <ActionIcon
              variant="outline"
              color="indigo"
              disabled={!(externalObj.bagelObjectKey && externalObj.isDisabled !== true) || loadingStates[externalObj.originObject] || isBagelObjectsLoading}
              onClick={() => handleOpenDrawer(externalObj)}
            >
              <IconAdjustments size={16} />
            </ActionIcon>
          </Tooltip>
        </td>
      </tr>
    );
  };

  return (
    <Box pos="relative">
      <FieldMappingDrawer
        opened={isFieldDrawerOpen}
        onClose={handleCloseDrawer}
        selectedObject={selectedObject}
        selectedBagelObject={selectedBagelObject}
        integrationKey={available.key}
        integrationName={available.name}
        available={available}
        connected={connected}
        updateExternalObject={updateExternalObject}
      />
      
      {/* Filter Modal */}
      {currentFilterObject && (
        <FilterPopup
          opened={filterModalOpen}
          onClose={handleCloseFilterModal}
          externalObj={currentFilterObject}
          integrationKey={available.key}
          updateExternalObject={updateExternalObject}
        />
      )}
      
      {/* Explore Fields Modal */}
      <ExploreFieldsModal
        opened={exploreModalOpen}
        onClose={handleCloseExploreModal}
        externalObject={exploreObjectKey ? externalObjects.find(obj => obj.originObject === exploreObjectKey) || null : null}
        integrationName={available.name}
        integrationKey={available.key}
      />
      <Stack spacing="xl">
        <Box>
          <Group position="apart" align="flex-start">
            <Box>
              <Text weight={500} size="md">Object Mappings</Text>
              <Text size="sm" color="dimmed" mb="xs">
                Configure how your {available.name} data syncs with Bagel by mapping objects between the systems.
              </Text>
              {externalObjects.length > 0 && (
                <Text size="sm" color="dimmed">
                  For instance, you can map {available.name} <Code>{externalObjects[0].label}</Code> to a Bagel object.            
                </Text>
              )}
            </Box>
            <Button
              leftIcon={<IconRefresh size={16} />}
              variant="subtle"
              color="indigo"
              onClick={handlePrepareMeta}
              loading={prepareMetaMutation.isPending}
              size="sm"
            >
              Prepare Meta
            </Button>
          </Group>
        </Box>

        <Paper withBorder>
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>{available.name} Objects</th>
                <th>Enable</th>
                <th>Refresh</th>
                <th>Explore</th>
                {available.key !== 'api' && <th>Get samples</th>}
                <th>Process</th>
                <th>Filter</th>
                <th>Bagel Objects</th>
                <th>Ready to Process</th>
                <th>Ready to Sync</th>
                <th style={{ textAlign: 'right' }}>Configure</th>
              </tr>
            </thead>
            <tbody>
              {externalObjects.length === 0 ? (
                <tr>
                  <td colSpan={available.key === 'api' ? 10 : 11} style={{ padding: 0, border: 'none' }}>
                    {renderEmptyState()}
                  </td>
                </tr>
              ) : (
                externalObjects.map((externalObj) => (
                  <MappingRow
                    key={externalObj.originObject}
                    externalObj={externalObj}
                    availableKey={available.key}
                    bagelObjects={bagelObjects}
                    loadingStates={loadingStates}
                    isBagelObjectsLoading={isBagelObjectsLoading}
                    isUpdatingMapping={isUpdatingMapping}
                    updateExternalObject={updateExternalObject}
                    handleOpenFilterModal={handleOpenFilterModal}
                    handleOpenExploreModal={handleOpenExploreModal}
                    handleOpenDrawer={handleOpenDrawer}
                    handleOpenRunModal={handleOpenRunModal}
                    activeTasks={activeTasksData?.tasks || []}
                  />
                ))
              )}
              {isAddMode && (
                <tr>
                  <td>
                    <Group spacing="sm" noWrap>
                      <SourceIcon sourceName={available.key} width={20} height={20} />
                      <Select
                        itemComponent={({ label, value, projectKey, originObject, name, ...rest }: any) => (
                          <Box {...rest}>
                            <Text size="sm" weight={500}>{name}</Text>
                            <Group spacing="xs" mt={4} noWrap>
                              {originObject && originObject !== name && (
                                <Code color="gray">
                                  {originObject}
                                </Code>
                              )}
                              {projectKey && (
                                <Code color="blue">
                                  <Group spacing={4}>
                                    <SourceIcon sourceName={available.key} width={10} height={10} />
                                    {projectKey}
                                  </Group>
                                </Code>
                              )}
                            </Group>
                          </Box>
                        )}
                        data={connected?.metaObject?.availableObjects?.map((obj: { id: string; name?: string; originObject?: string; extraParams?: { projectKey?: string } }) => {
                          const projectKey = obj.extraParams?.projectKey;
                          let label = obj.name || '';
                          
                          // Add originObject if different from name
                          if (obj.originObject && obj.originObject !== obj.name) {
                            label += ` (${obj.originObject})`;
                          }
                          
                          // Add projectKey if available
                          if (projectKey) {
                            label += ` [${projectKey}]`;
                          }
                          
                          return {
                            label,
                            value: obj.originObject || obj.name,
                            disabled: externalObjects.some(extObj => extObj.originObject === (obj.originObject || obj.name)),
                            projectKey, // Store for search and display
                            originObject: obj.originObject,
                            name: obj.name
                          };
                        }) || []}
                        value={newObjectMapping.objectId}
                        onChange={(value) => setNewObjectMapping(prev => ({ ...prev, objectId: value || '' }))}
                        placeholder="Select object"
                        style={{ minWidth: 200 }}
                        searchable
                        nothingFound="No objects found"
                        filter={(value, item) => {
                          const searchValue = value.toLowerCase();
                          return (
                            item.label?.toLowerCase().includes(searchValue) ||
                            (item.value as string)?.toLowerCase().includes(searchValue) ||
                            (item as any).projectKey?.toLowerCase().includes(searchValue) ||
                            (item as any).name?.toLowerCase().includes(searchValue)
                          );
                        }}
                      />
                    </Group>
                  </td>
                  <td>
                    <Switch checked={false} disabled />
                  </td>
                  <td>
                    <ActionIcon variant="outline" color="blue" disabled>
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </td>
                  <td>
                    <ActionIcon variant="outline" color="blue" disabled>
                      <IconSearch size={16} />
                    </ActionIcon>
                  </td>
                  {available.key !== 'api' && (
                    <td>
                      <ActionIcon variant="outline" color="teal" disabled>
                        <IconRun size={16} />
                      </ActionIcon>
                    </td>
                  )}
                  <td>
                    <ActionIcon variant="outline" color="gray" disabled>
                      <IconPlayerPlay size={16} />
                    </ActionIcon>
                  </td>
                  <td>
                    <ActionIcon variant="outline" color="blue" disabled>
                      <IconFilter size={16} />
                    </ActionIcon>
                  </td>
                  <td>
                    <Select
                      data={bagelObjects?.map((obj: BagelObjectConf) => ({
                        label: obj.label,
                        value: obj.key
                      })) || []}
                      value={newObjectMapping.bagelObjectKey}
                      onChange={(value) => setNewObjectMapping(prev => ({ ...prev, bagelObjectKey: value || '' }))}
                      placeholder="Select object"
                    />
                  </td>
                  <td>
                    <Switch checked={false} disabled />
                  </td>
                  <td>
                    <Switch checked={false} disabled />
                  </td>
                  <td style={{ justifyItems: 'right' }}>
                    <Group spacing="xs">
                      <Button 
                        size="xs" 
                        color="indigo"
                        onClick={handleSubmitNewMapping}
                        disabled={!newObjectMapping.objectId}
                        rightIcon={isAddingMapping ? <Loader size={14} color="white" /> : undefined}
                      >
                        Add
                      </Button>
                      <ActionIcon 
                        variant="outline"
                        color="gray" 
                        onClick={handleCancelAddMapping}
                      >
                        <IconX size={16} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Paper>
        {!isAddMode && available.key !== 'apollo' && (
          <Button 
            leftIcon={<IconPlus size={16} />} 
            onClick={handleAddNewMapping}
            variant="outline"
          >
            {externalObjects.length === 0 
              ? "Add new object" 
              : `Sync another ${connected?.name || available.name} object to Bagel`}
          </Button>
        )}
      </Stack>
      {/* Run Modal Popup (moved to IntegrationSyncRunsTimeline for reusability) */}
      <RunModal
        opened={runModalOpen}
        onClose={handleCloseRunModal}
        externalObjects={externalObjects}
        externalObject={currentRunObject}
        isLoadingObject={isLoadingObjects}
        selectedObjects={selectedObjects}
        setSelectedObjects={setSelectedObjects}
        onRun={handleRun}
        isRunning={isRunning}
        integrationKey={available.key}
        activeTasks={(activeTasksData?.tasks || []).filter((task: any) =>
          currentRunObject
            ? task.objectName === currentRunObject.originObject
            : true
        )}
      />
    </Box>
  );
};

export default MappingsTab; 
